import * as React from 'react';
import { RoleDefinition, RolePermission } from '../types';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, Info, Lock } from 'lucide-react';

interface RoleManagementProps {
  roles: RoleDefinition[];
  onUpdateRole?: (roleId: string, permissions: RolePermission[]) => void;
  isLoading?: boolean;
}

export function RoleManagement({ roles, onUpdateRole, isLoading = false }: RoleManagementProps) {
  const [selectedRole, setSelectedRole] = React.useState<RoleDefinition>(roles[0]);
  const [editedPermissions, setEditedPermissions] = React.useState<RolePermission[]>(
    roles[0]?.permissions || []
  );
  const [hasChanges, setHasChanges] = React.useState(false);

  React.useEffect(() => {
    // Reset edited permissions when selected role changes
    setEditedPermissions(selectedRole?.permissions || []);
    setHasChanges(false);
  }, [selectedRole]);

  const handlePermissionChange = (permissionId: string, isGranted: boolean) => {
    const updatedPermissions = editedPermissions.map((permission) =>
      permission.id === permissionId
        ? { ...permission, isGranted }
        : permission
    );
    setEditedPermissions(updatedPermissions);
    setHasChanges(true);
  };

  const handleSave = () => {
    if (onUpdateRole && selectedRole) {
      onUpdateRole(selectedRole.id, editedPermissions);
      setHasChanges(false);
    }
  };

  const handleReset = () => {
    setEditedPermissions(selectedRole?.permissions || []);
    setHasChanges(false);
  };

  const permissionsByCategory = React.useMemo(() => {
    const categories: Record<string, RolePermission[]> = {};
    
    editedPermissions.forEach((permission) => {
      if (!categories[permission.category]) {
        categories[permission.category] = [];
      }
      categories[permission.category].push(permission);
    });
    
    return categories;
  }, [editedPermissions]);

  if (!roles.length) {
    return <div>No roles defined.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Role Management</h2>
          <p className="text-muted-foreground">
            Configure permissions for each user role in the system.
          </p>
        </div>
        <TabsList className="self-start">
          {roles.map((role) => (
            <TabsTrigger
              key={role.id}
              value={role.id}
              onClick={() => setSelectedRole(role)}
              className="min-w-24"
            >
              {role.name}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>

      {selectedRole && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{selectedRole.name}</CardTitle>
                <CardDescription>{selectedRole.description}</CardDescription>
              </div>
              {selectedRole.isSystemRole && (
                <Badge variant="outline" className="gap-1">
                  <Lock className="h-3 w-3" />
                  System Role
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {selectedRole.isSystemRole && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>System Role</AlertTitle>
                <AlertDescription>
                  This is a system-defined role. Some permissions cannot be modified as they are essential for the role's functionality.
                </AlertDescription>
              </Alert>
            )}

            {Object.entries(permissionsByCategory).map(([category, permissions]) => (
              <div key={category} className="space-y-3">
                <h3 className="text-lg font-medium capitalize">{category}</h3>
                <Separator />
                <div className="space-y-2">
                  {permissions.map((permission) => {
                    const isDisabled = selectedRole.isSystemRole && 
                      ((selectedRole.id === 'admin' && permission.isGranted) || 
                       (selectedRole.id === 'subscriber' && category === 'documents' && permission.id === 'documents.view'));
                    
                    return (
                      <div
                        key={permission.id}
                        className="flex items-center justify-between rounded-lg border p-3"
                      >
                        <div className="space-y-0.5">
                          <div className="font-medium">{permission.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {permission.description}
                          </div>
                        </div>
                        <Switch
                          checked={permission.isGranted}
                          onCheckedChange={(checked) =>
                            handlePermissionChange(permission.id, checked)
                          }
                          disabled={isDisabled || isLoading}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </CardContent>
          <CardFooter className="flex justify-between border-t px-6 py-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Info className="h-4 w-4" />
              <span>
                Changes to role permissions affect all users with this role.
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={!hasChanges || isLoading}
              >
                Reset
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasChanges || isLoading}
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </CardFooter>
        </Card>
      )}
    </div>
  );
}
